<script setup>
import { ref, onMounted } from 'vue';
import { CountryService } from '../services/CountryService.js';

const props = defineProps({
    country: { type: String, default: '' },
    city: { type: String, default: '' },
    showCountry: { type: Boolean, default: true },
    showCity: { type: Boolean, default: true },
    countryPlaceholder: { type: String, default: 'Odaberite državu' },
    cityPlaceholder: { type: String, default: 'Odaberite grad' }
});

const emit = defineEmits(['update:country', 'update:city']);

// Rezervna vrijednost ako lista država (backend geography proxy) nije dostupna,
// da bi se gradovi Crne Gore ipak mogli učitati za polja bez izbora države.
const MONTENEGRO_FALLBACK = { name: 'Crna Gora', nameEnglish: 'Montenegro', code: 'ME' };

const countries = ref([]);
const cities = ref([]);
const selectedCountry = ref(null);
// Grad je editable: može se izabrati sa liste ili ručno upisati
// (npr. ako servis za gradove ne radi ili mjesto nije na listi).
const selectedCity = ref(props.city || null);
const loadingCountries = ref(false);
const loadingCities = ref(false);

onMounted(async () => {
    loadingCountries.value = true;
    const data = await CountryService.getCountries();
    countries.value = data;
    loadingCountries.value = false;

    if (props.country) {
        const match = data.find((c) => c.name === props.country || c.nameEnglish === props.country || c.code === props.country);
        if (match) {
            selectedCountry.value = match;
            await loadCities(match.nameEnglish);
            matchCity();
        }
    } else if (props.showCity) {
        const me = data.find((c) => c.code === 'ME') || (props.showCountry ? null : MONTENEGRO_FALLBACK);
        if (me) {
            if (props.showCountry) {
                selectedCountry.value = me;
                emit('update:country', me.name);
            }
            await loadCities(me.nameEnglish);
            matchCity();
        }
    }
});

function matchCity() {
    if (props.city && cities.value.length) {
        const match = cities.value.find((c) => c.toLowerCase() === props.city.toLowerCase());
        selectedCity.value = match || props.city;
    }
}

async function loadCities(englishName) {
    if (!englishName) {
        cities.value = [];
        return;
    }
    loadingCities.value = true;
    cities.value = await CountryService.getCities(englishName);
    loadingCities.value = false;
}

function onCountryChange(event) {
    const c = event.value;
    selectedCity.value = null;
    emit('update:city', '');
    if (c) {
        emit('update:country', c.name);
        loadCities(c.nameEnglish);
    } else {
        emit('update:country', '');
        cities.value = [];
    }
}

function onCityChange(event) {
    emit('update:city', event.value || '');
}
</script>

<template>
    <div class="location-select">
        <Dropdown
            v-if="showCountry"
            v-model="selectedCountry"
            :options="countries"
            optionLabel="name"
            :placeholder="countryPlaceholder"
            filter
            showClear
            :loading="loadingCountries"
            class="inline-input"
            @change="onCountryChange"
        />
        <Dropdown
            v-if="showCity"
            v-model="selectedCity"
            :options="cities"
            :placeholder="cityPlaceholder"
            editable
            showClear
            :loading="loadingCities"
            :disabled="showCountry && countries.length > 0 && !selectedCountry"
            :aria-label="cityPlaceholder"
            class="inline-input"
            @change="onCityChange"
        />
    </div>
</template>

<style scoped>
.location-select {
    display: contents;
}
</style>
